import React from "react";
import { compose, withHandlers, withStateHandlers, withProps } from "recompose";
import { withXeroErrors } from "components";
import {
  FormGroup,
  SubmitButton,
  Page,
  DatePicker,
  onMount,
  required,
  NumberField,
  withPopover,
  withLocalForm,
} from "components/common";
import { Control, actions } from "react-redux-form";
import { Row, Col, PopoverBody, PopoverHeader } from "reactstrap";
import Select from "react-select";
import { get, compact } from "lodash";
import { CurrencySymbol, formatCurrency } from "services/util";
import { NotificationManager } from "react-notifications";
import { withTranslation, Trans } from "react-i18next";
import invariant from "invariant";
import { withOrganisationContextConsumer } from "../contexts/OrganisationContext";
import { isCurrency } from "../services/util";
import { PayrollClient } from "services";

export const ReceivePayment = ({
  accounts,
  handleSubmit,
  XeroErrors,
  organisation,
  invoice,
  creditNote,
  Popover,
  setIsPopoverOpen,
  LocalForm,
  overpaymentAmount,
  handleConfirmOverpayment,
  setOverpaymentDescription,
  overpaymentDescription,
  t,
  title,
  payable,
  currencyCode,
  paymentType,
}) => {
  const formatDate = (dateString) => {
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));

    return formattedDate.replace(/(\d)(st|nd|rd|th)/, (_, digit, suffix) => `${digit}<sup>${suffix}</sup>`);
  };

  const amountToBePaid = invoice?.AmountDue || creditNote?.RemainingCredit || ""
  const currencyRate = parseFloat(invoice?.CurrencyRate).toFixed(2) || ""

  const urlChange = (() => {
    switch (invoice?.Type) {
      case "ACCREC":
        return "Receivable";
      case "ACCPAY":
        return "Payable";
      default:
        return null;
    }
  })();

  const today = new Date();

  const dateCurrent = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const currentDate = dateCurrent(today);

  const listItemStyle = {
    marginBottom: 10,
  };

  return (

    <Page title={title}>
      <XeroErrors />
      <LocalForm
        validators={{
          Date: { required },
          "Account.AccountID": { required },
          Amount: (v) => isCurrency(v, { min: 0.01 }),
          CurrencyRate: (v) =>
            currencyCode === organisation.BaseCurrency || required(v),
        }}
        onSubmit={handleSubmit}
      >
        <div className="d-inline-block">
          <Row>
            <Col xs="auto">
              <FormGroup
                label={t("Amount Paid")}
                model=".Amount"
                style={{ width: "9rem" }}
              >
                <Control.text type="number" step="any" model=".Amount" defaultValue={amountToBePaid} />
              </FormGroup>
            </Col>
            {currencyCode !== organisation.BaseCurrency ? (
              <Col xs="auto">
                <FormGroup label="Exchange Rate" model=".CurrencyRate">
                  <div className="d-flex align-items-center">
                    1 <CurrencySymbol currency={organisation.BaseCurrency} /> ={" "}
                    <NumberField
                      step="any"
                      model=".CurrencyRate"
                      className="form-control mx-1"
                      style={{ maxWidth: "5rem" }}
                      defaultValue={currencyRate}
                    />{" "}
                    <CurrencySymbol currency={currencyCode} />
                  </div>
                </FormGroup>
              </Col>
            ) : null}
            <Col xs="auto">
              <FormGroup
                label="Date Paid"
                model=".Date"
                style={{ width: "8rem" }}
              >
                <DatePicker model=".Date" className="form-control" defaultValue={currentDate} />
              </FormGroup>
            </Col>
            <Col xs="auto">
              <FormGroup
                label={payable ? "Paid From" : "Account"}
                model=".Account.AccountID"
                addControlClass={false}
              >
                <Control
                  style={{ minWidth: "12rem", backgroundColor: "#F4F7FE" }}
                  simpleValue
                  labelKey="Name"
                  valueKey="AccountID"
                  component={Select}
                  model=".Account.AccountID"
                  options={accounts}
                  placeholder={t('Select...')}
                />
              </FormGroup>
            </Col>
            <Col xs="auto">
              <FormGroup label="Reference" model=".Reference">
                <Control.text model=".Reference" />
              </FormGroup>
              <Popover
                placement="top"
                target="add-payment"
                toggle={() => {
                  setIsPopoverOpen(false);
                }}
              >
                <PopoverHeader>
                  <Trans>Overpayment</Trans>
                </PopoverHeader>
                <PopoverBody>
                  <Trans>
                    <p>
                      The payment you entered exceeds the amount owed by{" "}
                      <strong>
                        <CurrencySymbol currency={currencyCode} />
                        {{ overpaymentAmount: formatCurrency(overpaymentAmount) }}{" "}
                      </strong>
                      . Would you like to create an overpayment?
                    </p>
                  </Trans>
                  <div style={{ fontSize: "0.9em", }}>
                    <a href={`https://go.xero.com/Accounts${urlChange}/View.aspx?InvoiceID=${invoice?.InvoiceID || invoice?.CreditNoteID}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      Use Xero (For CN Generation and Refund Option)
                    </a>
                  </div>
                  <hr />
                  <FormGroup
                    label="Description"
                    className={!overpaymentDescription ? "has-danger" : ""}
                  >
                    <input
                      type="text"
                      className="form-control"
                      onChange={(e) => setOverpaymentDescription(e.target.value)}
                      value={overpaymentDescription}
                    />
                  </FormGroup>
                  <div className="d-flex justify-content-end my-2">
                    <SubmitButton
                      color="success"
                      onClick={handleConfirmOverpayment}
                      className="btn-sm"
                    >
                      Confirm
                    </SubmitButton>
                  </div>
                </PopoverBody>
              </Popover>
            </Col>
            <Col xs="auto" className="align-self-end">
              <FormGroup>
                <SubmitButton color="success" id="add-payment">
                  Add {paymentType || "Payment"}
                </SubmitButton>
              </FormGroup>
            </Col>
          </Row>
        </div>
      </LocalForm>
      {invoice && (
        <div style={{ paddingTop: "1.3em", fontSize: "1.2em" }}>
          <div>
            <ul style={{ listStyleType: 'none', padding: 5 }}>
              <li style={listItemStyle}><Trans>Payment Due Date</Trans>-<strong>{formatDate(invoice.DueDate)}</strong></li>
              <li style={listItemStyle}><Trans>Payment Total Amount Due</Trans>-<strong>{formatCurrency(amountToBePaid)}</strong></li>
              {invoice.AmountPaid > 0 ? (
                <li style={listItemStyle}><Trans>Total Amount Paid</Trans>-<strong>{formatCurrency(invoice.AmountPaid)}</strong></li>
              ) : (
                <li style={{ ...listItemStyle, color: "#6F6F7B", fontStyle: "italic" }}>
                  <Trans>There have been no payments yet</Trans>.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </Page>
  )
};

export const enhance = compose(
  withOrganisationContextConsumer,
  withProps(({ invoice, creditNote }) => {
    invariant(
      compact([invoice, creditNote]).length === 1,
      "invoice or creditNote must be supplied"
    );

    const { CurrencyCode, Type } = invoice || creditNote;

    const [title, payable] =
      {
        ACCREC: ["Receive a Payment", false],
        ACCPAY: ["Make a payment", true],
        ACCRECCREDIT: ["Cash refund", false],
        'RECEIVE-OVERPAYMENT': ["Cash refund", false],
        'RECEIVE-PREPAYMENT': ["Cash refund", false],
        ACCPAYCREDIT: ["Receive a Cash refund", true],
      }[Type] ?? [];

    return {
      title,
      payable,
      currencyCode: CurrencyCode,
    };
  }),
  withTranslation(),
  onMount(
    ({ xero, fetchAccounts }) =>
      () =>
        Promise.props({
          accounts: fetchAccounts().filter(
            (i) => i.Type === "BANK" || i.EnablePaymentsToAccount
          ),
        })
  ),
  withStateHandlers(
    {
      overpaymentDescription: "",
    },
    {
      setOverpaymentAmount: () => (overpaymentAmount) => ({
        overpaymentAmount,
      }),
      setOverpaymentDescription: () => (overpaymentDescription) => ({
        overpaymentDescription,
      }),
    }
  ),
  withLocalForm({
    initialState: ({ creditNote, invoice }) => {
      return {
        Amount: invoice?.AmountDue ?? creditNote?.RemainingCredit ?? ""
      };
    }
  }),
  withXeroErrors(),
  withPopover(),
  withHandlers({
    handleConfirmOverpayment:
      ({
        overpaymentAmount,
        state,
        setXeroErrors,
        overpaymentDescription,
        dispatch,
        t,
        invoice: { InvoiceID, Contact, AmountDue, Type },
        xero,
      }) =>
        async () => {
          if (!overpaymentDescription) {
            NotificationManager.error("Please enter a description for the Overpayment")
            return;
          }

          const changedType = (() => {
            if (Type === "ACCREC" || Type === "ACCRECCREDIT") {
              return "RECEIVE-OVERPAYMENT";
            } else if (Type === "ACCPAY" || Type === "ACCPAYCREDIT") {
              return "SPEND-OVERPAYMENT";
            } else {
              return null;
            }
          })();

          await xero
            .post("banktransactions", {
              Type: changedType,
              Contact,
              BankAccount: state.Account,
              LineItems: [
                {
                  Description: overpaymentDescription,
                  LineAmount: overpaymentAmount,
                },
              ],
            })
            .then(() => {
              NotificationManager.success(t("Overpayment is successfully added"));
              dispatch(
                actions.change(
                  "local.Amount",
                  AmountDue
                  /*{parseFloat(state.Amount - overpaymentAmount).toFixed(2)}*/
                )
              );
              dispatch(actions.submit("local"));
              return null;
            }, setXeroErrors);
        },
    handleSubmit:
      ({
        invoice,
        creditNote,
        xero,
        dispatch,
        onPaymentAdded,
        setXeroErrors,
        setIsPopoverOpen,
        setOverpaymentAmount,
        t,
      }) =>
        async (item) => {
          const amounts = item.Amount;
          const amount = parseFloat(amounts).toFixed(2)
          const payroll = PayrollClient()

          if (invoice) {
            const { AmountDue } = invoice;
            const overpaymentAmount = amount - AmountDue;
            if (overpaymentAmount > 0) {
              setIsPopoverOpen(true);
              setOverpaymentAmount(overpaymentAmount);
              dispatch(actions.setSubmitted("local"));
              return;
            }
          }

          setXeroErrors(null);

          const document = invoice || creditNote
          const docType = {
            ACCREC: "Invoice",
            ACCPAY: "Invoice",
            ACCRECCREDIT: "CreditNote",
            ACCPAYCREDIT: "CreditNote",
            'RECEIVE-OVERPAYMENT': "Overpayment",
            'RECEIVE-PREPAYMENT': "Prepayment",
          }[document.Type]

          await xero
            .post("payments", {
              ...item,
              ...(docType ? { [docType]: {[`${docType}ID`]: document[`${docType}ID`] }} : {}),
            })
            .then(
              (resp) => {
                NotificationManager.success(t("Payment successfully completed"));
                const inv = resp && resp.data?.Invoice
                const id = inv?.Reference?.split("-")?.[1]

                dispatch(actions.reset("local"));
                onPaymentAdded && onPaymentAdded(resp.data);
                return resp;
              },
              (err) => {
                const errors = get(err, "response.data.detail");
                if (errors) {
                  setXeroErrors(err);
                } else {
                  throw err;
                }
              }
            )
            .finally(() => dispatch(actions.setSubmitted("local")));
        },
  })
);

export default enhance(ReceivePayment);
